import { useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import type { DashboardTab } from "./DashboardTabs";

const TAB_QUERY_PARAM = "tab";
const DEFAULT_TAB: DashboardTab = "summary";

const isDashboardTab = (value: string | null): value is DashboardTab =>
  value === "summary" || value === "auditLog";

/**
 * Keeps the active dashboard tab in sync with the URL query string (?tab=...)
 * so the selection survives reloads and is shareable/deep-linkable.
 *
 * Mirrors the `useState` signature so it can be dropped in place of local state.
 */
export function useDashboardTab(): [DashboardTab, (tab: DashboardTab) => void] {
  const history = useHistory();
  const location = useLocation();

  const param = new URLSearchParams(location.search).get(TAB_QUERY_PARAM);
  const activeTab = isDashboardTab(param) ? param : DEFAULT_TAB;

  const setActiveTab = useCallback(
    (tab: DashboardTab) => {
      const search = new URLSearchParams(location.search);
      if (tab === DEFAULT_TAB) {
        search.delete(TAB_QUERY_PARAM);
      } else {
        search.set(TAB_QUERY_PARAM, tab);
      }
      const nextSearch = search.toString();
      history.push({
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
        state: location.state,
      });
    },
    [history, location.pathname, location.search, location.state],
  );

  return [activeTab, setActiveTab];
}
